import { useEffect } from "react";
import { useQueries, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { leadListsApi } from "../../../libs/landvoice-api";
import { SavedSearch } from "../../../types/neighborhoodSearch";
import { getNsSavedSearchCacheKey } from "../constants/cacheKeys";
import { transformSavedSearch } from "../utils/transformSavedSearch";

export const useSavedSearches = (
  ids: string[] | string
): UseQueryResult<SavedSearch[]> => {
  const queryClient = useQueryClient();
  const results = useQueries({
    queries: (Array.isArray(ids) ? ids : [ids]).map((id) => ({
      queryKey: getNsSavedSearchCacheKey(id),
      queryFn: async () => {
        const response = await leadListsApi.getListObj(id);
        return transformSavedSearch(response?.data as SavedSearch);
      },
      enabled: !!id,
      staleTime: 0, // Always refetch when the component mounts
    })),
  });

  // Prefetch the data to ensure it's in the cache
  useEffect(() => {
    (Array.isArray(ids) ? ids : [ids]).forEach((id) => {
      if (id) {
        queryClient.prefetchQuery({
          queryKey: getNsSavedSearchCacheKey(id),
          queryFn: async () => {
            const response = await leadListsApi.getListObj(id);
            return transformSavedSearch(response?.data as SavedSearch);
          },
        });
      }
    });
  }, [ids, queryClient]);

  return {
    data: results.flatMap((result) => result.data || []),
    isPending: results.some((result) => result.isPending),
    isError: results.some((result) => result.isError),
    isLoading: results.some((result) => result.isLoading),
  } as UseQueryResult<SavedSearch[]>;
};